import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Shield, Lock, Eye, AlertTriangle } from 'lucide-react';

interface LexMessageRendererProps {
  content: string;
  className?: string;
  enableLegalFormatting?: boolean;
  enableSmartEmojis?: boolean;
  role?: 'user' | 'assistant' | 'system';
}

/**
 * Smart Emoji/Intent Parsing System
 */

// Comprehensive emoji/intent patterns for legal AI responses
const EMOJI_INTENT_PATTERNS = [
  // General Status & Actions
  { pattern: /\[success\]/gi, emoji: '✅', description: 'Success/Completed' },
  { pattern: /\[completed?\]/gi, emoji: '✅', description: 'Completed' },
  { pattern: /\[done\]/gi, emoji: '✅', description: 'Done' },
  { pattern: /\[approved?\]/gi, emoji: '✅', description: 'Approved' },
  { pattern: /\[valid\]/gi, emoji: '✅', description: 'Valid' },
  
  { pattern: /\[warning\]/gi, emoji: '⚠️', description: 'Warning' },
  { pattern: /\[caution\]/gi, emoji: '⚠️', description: 'Caution' },
  { pattern: /\[attention\]/gi, emoji: '⚠️', description: 'Attention Required' },
  { pattern: /\[review\]/gi, emoji: '⚠️', description: 'Needs Review' },
  { pattern: /\[risk\]/gi, emoji: '⚠️', description: 'Risk Assessment' },
  
  { pattern: /\[error\]/gi, emoji: '❌', description: 'Error' },
  { pattern: /\[failed?\]/gi, emoji: '❌', description: 'Failed' },
  { pattern: /\[rejected?\]/gi, emoji: '❌', description: 'Rejected' },
  { pattern: /\[invalid\]/gi, emoji: '❌', description: 'Invalid' },
  { pattern: /\[missing\]/gi, emoji: '❌', description: 'Missing' },
  
  { pattern: /\[tip\]/gi, emoji: '💡', description: 'Tip/Suggestion' },
  { pattern: /\[idea\]/gi, emoji: '💡', description: 'Idea' },
  { pattern: /\[suggestion\]/gi, emoji: '💡', description: 'Suggestion' },
  { pattern: /\[recommendation\]/gi, emoji: '💡', description: 'Recommendation' },
  { pattern: /\[insight\]/gi, emoji: '💡', description: 'Insight' },
  
  // Legal-Specific Intents
  { pattern: /\[document\]/gi, emoji: '📄', description: 'Document Reference' },
  { pattern: /\[contract\]/gi, emoji: '📄', description: 'Contract' },
  { pattern: /\[agreement\]/gi, emoji: '📄', description: 'Agreement' },
  { pattern: /\[filing\]/gi, emoji: '📋', description: 'Filing/Form' },
  { pattern: /\[petition\]/gi, emoji: '📋', description: 'Petition' },
  { pattern: /\[motion\]/gi, emoji: '📋', description: 'Motion' },
  
  { pattern: /\[search\]/gi, emoji: '🔍', description: 'Search/Investigation' },
  { pattern: /\[research\]/gi, emoji: '🔍', description: 'Legal Research' },
  { pattern: /\[analysis\]/gi, emoji: '🔍', description: 'Analysis' },
  { pattern: /\[investigation\]/gi, emoji: '🔍', description: 'Investigation' },
  
  { pattern: /\[deadline\]/gi, emoji: '⏰', description: 'Deadline' },
  { pattern: /\[urgent\]/gi, emoji: '⏰', description: 'Urgent' },
  { pattern: /\[time[- ]sensitive\]/gi, emoji: '⏰', description: 'Time Sensitive' },
  { pattern: /\[priority\]/gi, emoji: '⏰', description: 'High Priority' },
  
  { pattern: /\[client\]/gi, emoji: '👤', description: 'Client' },
  { pattern: /\[attorney\]/gi, emoji: '⚖️', description: 'Attorney' },
  { pattern: /\[counsel\]/gi, emoji: '⚖️', description: 'Legal Counsel' },
  { pattern: /\[court\]/gi, emoji: '🏛️', description: 'Court' },
  { pattern: /\[judge\]/gi, emoji: '👨‍⚖️', description: 'Judge' },
  
  { pattern: /\[compliance\]/gi, emoji: '📊', description: 'Compliance' },
  { pattern: /\[regulation\]/gi, emoji: '📊', description: 'Regulation' },
  { pattern: /\[statute\]/gi, emoji: '📚', description: 'Statute' },
  { pattern: /\[case[- ]law\]/gi, emoji: '📚', description: 'Case Law' },
  { pattern: /\[precedent\]/gi, emoji: '📚', description: 'Legal Precedent' },
  
  { pattern: /\[billing\]/gi, emoji: '💰', description: 'Billing' },
  { pattern: /\[invoice\]/gi, emoji: '💰', description: 'Invoice' },
  { pattern: /\[payment\]/gi, emoji: '💰', description: 'Payment' },
  { pattern: /\[fee\]/gi, emoji: '💰', description: 'Legal Fee' },
  
  { pattern: /\[meeting\]/gi, emoji: '🤝', description: 'Meeting' },
  { pattern: /\[conference\]/gi, emoji: '🤝', description: 'Conference' },
  { pattern: /\[negotiation\]/gi, emoji: '🤝', description: 'Negotiation' },
  { pattern: /\[mediation\]/gi, emoji: '🤝', description: 'Mediation' },
  
  { pattern: /\[action[- ]required\]/gi, emoji: '🎯', description: 'Action Required' },
  { pattern: /\[todo\]/gi, emoji: '📝', description: 'To-Do Item' },
  { pattern: /\[task\]/gi, emoji: '📝', description: 'Task' },
  { pattern: /\[follow[- ]up\]/gi, emoji: '📝', description: 'Follow-up' },
  
  // Emotional/Status Context
  { pattern: /\[positive\]/gi, emoji: '😊', description: 'Positive Outcome' },
  { pattern: /\[negative\]/gi, emoji: '😟', description: 'Negative Outcome' },
  { pattern: /\[neutral\]/gi, emoji: '😐', description: 'Neutral Status' },
  { pattern: /\[progress\]/gi, emoji: '📈', description: 'Progress' },
  { pattern: /\[improvement\]/gi, emoji: '📈', description: 'Improvement' },
  
  // Security & Confidentiality
  { pattern: /\[confidential\]/gi, emoji: '🔒', description: 'Confidential' },
  { pattern: /\[privileged\]/gi, emoji: '🛡️', description: 'Attorney-Client Privilege' },
  { pattern: /\[secure\]/gi, emoji: '🔒', description: 'Secure' },
  { pattern: /\[protected\]/gi, emoji: '🛡️', description: 'Protected Information' },
];

// Smart emoji parser with legal context awareness
const withSmartEmojis = (text: string, enableSmartEmojis: boolean = true): string => {
  if (!enableSmartEmojis) return text;
  
  let processedText = text;
  
  // Apply all emoji/intent patterns
  EMOJI_INTENT_PATTERNS.forEach(({ pattern, emoji }) => {
    processedText = processedText.replace(pattern, emoji);
  });
  
  // Smart context-aware replacements
  processedText = processedText
    // Legal status patterns
    .replace(/\b(compliant|in compliance)\b/gi, '✅ $1')
    .replace(/\b(non[- ]?compliant|violation)\b/gi, '❌ $1')
    .replace(/\b(requires review|needs attention)\b/gi, '⚠️ $1')
    .replace(/\b(recommend|suggest)\b/gi, '💡 $1')
    
    // Action patterns
    .replace(/\b(file by|due by|deadline)\b/gi, '⏰ $1')
    .replace(/\b(client meeting|client conference)\b/gi, '👤 $1')
    .replace(/\b(court hearing|court date)\b/gi, '🏛️ $1')
    .replace(/\b(legal research|case research)\b/gi, '🔍 $1')
    
    // Document patterns
    .replace(/\b(contract review|agreement review)\b/gi, '📄 $1')
    .replace(/\b(filing|motion filing)\b/gi, '📋 $1')
    .replace(/\b(invoice|billing)\b/gi, '💰 $1')
    
    // Time-sensitive patterns
    .replace(/\b(urgent|asap|immediately)\b/gi, '⏰ $1')
    .replace(/\b(high priority|critical)\b/gi, '🎯 $1')
    
    // Status patterns
    .replace(/\b(completed|finished|done)\b/gi, '✅ $1')
    .replace(/\b(pending|in progress)\b/gi, '🔄 $1')
    .replace(/\b(failed|error|issue)\b/gi, '❌ $1');
  
  return processedText;
};

/**
 * Contextual Styling System for Chat Roles
 */

// Role-based styling configurations
const ROLE_STYLING = {
  assistant: {
    container: 'bg-lex-accent-soft dark:bg-lex-accent/10 border-l-4 border-lex-accent',
    textColor: 'text-gray-800 dark:text-gray-200',
    accentColor: 'text-lex-accent dark:text-lex-accent',
    icon: '🤖',
    label: 'AI Assistant',
    shadow: 'shadow-lex-accent/10',
    prose: 'prose-blue dark:prose-invert'
  },
  user: {
    container: 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-gray-800 dark:to-gray-750 border-l-4 border-slate-400 dark:border-slate-500',
    textColor: 'text-gray-900 dark:text-gray-100',
    accentColor: 'text-slate-700 dark:text-slate-300',
    icon: '👤',
    label: 'User',
    shadow: 'shadow-slate-100/50 dark:shadow-none',
    prose: 'prose-slate dark:prose-invert'
  },
  system: {
    container: 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-l-4 border-amber-400 dark:border-amber-500',
    textColor: 'text-gray-800 dark:text-gray-200',
    accentColor: 'text-amber-700 dark:text-amber-400',
    icon: '⚙️',
    label: 'System',
    shadow: 'shadow-amber-100/50 dark:shadow-none',
    prose: 'prose-amber dark:prose-invert'
  }
} as const;

// Get contextual styling based on role
const getContextualStyling = (role: 'user' | 'assistant' | 'system' = 'assistant') => {
  return ROLE_STYLING[role];
};

// Enhanced markdown components with role-aware styling
const getContextualMarkdownComponents = (role: 'user' | 'assistant' | 'system' = 'assistant') => {
  const styling = getContextualStyling(role);
  
  return {
    // Custom heading styles with role-aware colors
    h1: ({ children }: any) => (
      <h1 className={`text-lg font-bold ${styling.accentColor} mb-3 mt-4 first:mt-0`}>
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className={`text-base font-semibold ${styling.accentColor} mb-2 mt-3 first:mt-0`}>
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className={`text-sm font-semibold ${styling.accentColor} mb-2 mt-3 first:mt-0`}>
        {children}
      </h3>
    ),
    
    // Paragraph styling with role-aware text color
    p: ({ children }: any) => (
      <p className={`text-sm ${styling.textColor} mb-3 last:mb-0 leading-relaxed`}>
        {children}
      </p>
    ),
    
    // Strong and emphasis with role-aware colors
    strong: ({ children }: any) => (
      <strong className={`font-semibold ${styling.accentColor}`}>{children}</strong>
    ),
    em: ({ children }: any) => (
      <em className={`italic ${styling.textColor} opacity-80`}>{children}</em>
    ),
    
    // Links with role-aware theming
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${role === 'assistant' ? 'text-lex-accent hover:text-lex-accent-hover' :
                    role === 'user' ? 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200' :
                    'text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300'} underline transition-colors`}
      >
        {children}
      </a>
    ),
    
    // Code blocks with role-aware styling
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <div className="my-3 first:mt-0 last:mb-0">
          <pre className={`${role === 'assistant' ? 'bg-lex-accent-hover' :
                           role === 'user' ? 'bg-slate-800' : 
                           'bg-amber-900'} text-gray-100 p-3 rounded-lg overflow-x-auto`}>
            <code 
              className="text-xs font-mono leading-relaxed"
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </code>
          </pre>
        </div>
      ) : (
        <code 
          className={`${role === 'assistant' ? 'bg-lex-accent-soft dark:bg-lex-accent/15 text-lex-accent' :
                       role === 'user' ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200' :
                       'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'} px-1.5 py-0.5 rounded text-xs font-mono`}
          {...props}
        >
          {children}
        </code>
      );
    },
    
    // Lists with role-aware text color
    ul: ({ children }: any) => (
      <ul className={`list-disc list-inside mb-3 space-y-1 text-sm ${styling.textColor}`}>
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className={`list-decimal list-inside mb-3 space-y-1 text-sm ${styling.textColor}`}>
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="leading-relaxed">{children}</li>
    ),
    
    // Blockquotes with role-aware theming
    blockquote: ({ children }: any) => (
      <blockquote className={`border-l-4 ${role === 'assistant' ? 'border-lex-accent text-lex-accent' :
                                          role === 'user' ? 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300' :
                                          'border-amber-200 dark:border-amber-600 text-amber-700 dark:text-amber-400'} pl-4 my-3 italic`}>
        {children}
      </blockquote>
    ),
    
    // Tables with role-aware styling
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 text-xs">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className={`${role === 'assistant' ? 'bg-lex-accent-soft dark:bg-lex-accent/10' :
                          role === 'user' ? 'bg-slate-50 dark:bg-slate-800' :
                          'bg-amber-50 dark:bg-amber-900/20'}`}>{children}</thead>
    ),
    tbody: ({ children }: any) => (
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">{children}</tbody>
    ),
    tr: ({ children }: any) => (
      <tr>{children}</tr>
    ),
    th: ({ children }: any) => (
      <th className={`px-3 py-2 text-left font-medium ${styling.accentColor} uppercase tracking-wider`}>
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className={`px-3 py-2 whitespace-nowrap ${styling.textColor}`}>
        {children}
      </td>
    ),
    
    // Horizontal rules with role-aware color
    hr: () => (
      <hr className={`my-4 ${role === 'assistant' ? 'border-lex-accent' :
                              role === 'user' ? 'border-slate-200 dark:border-slate-600' :
                              'border-amber-200 dark:border-amber-600'}`} />
    ),
  };
};

/**
 * Legal Auto-Linking System
 */

// Legal database URL generators
const LEGAL_URL_GENERATORS = {
  // California Codes
  'cal-ccp': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=CCP`,
  'cal-civ': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=CIV`,
  'cal-bus': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=BPC`,
  'cal-corp': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=CORP`,
  'cal-evid': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=EVID`,
  'cal-fam': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=FAM`,
  'cal-prob': (section: string) => `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=${section}&lawCode=PROB`,
  
  // Federal Codes
  'usc': (title: string, section: string) => `https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title${title}-section${section}`,
  'cfr': (title: string, section: string) => `https://www.ecfr.gov/current/title-${title}/section-${title}.${section}`,
  
  // Case Law
  'case-search': (caseName: string) => `https://scholar.google.com/scholar_case?q=${encodeURIComponent(caseName)}`,
};

// Legal reference patterns with auto-linking
const LEGAL_REFERENCE_PATTERNS = [
  // California Code of Civil Procedure
  {
    pattern: /\b(?:California\s+)?(?:Code\s+of\s+)?Civil\s+Procedure\s*(?:\(CCP\))?\s*§?\s*(\d+(?:\.\d+)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['cal-ccp'](match[1]),
      text: match[0],
      title: `California Code of Civil Procedure § ${match[1]}`
    })
  },
  
  // California Civil Code
  {
    pattern: /\b(?:California\s+)?Civil\s+Code\s*(?:\(Civ\.?\s*Code\))?\s*§?\s*(\d+(?:\.\d+)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['cal-civ'](match[1]),
      text: match[0],
      title: `California Civil Code § ${match[1]}`
    })
  },
  
  // California Business & Professions Code
  {
    pattern: /\b(?:California\s+)?(?:Bus\.?\s*&?\s*Prof\.?\s*|Business\s+and\s+Professions)\s+Code\s*(?:\(B&P\))?\s*§?\s*(\d+(?:\.\d+)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['cal-bus'](match[1]),
      text: match[0],
      title: `California Business & Professions Code § ${match[1]}`
    })
  },
  
  // California Evidence Code
  {
    pattern: /\b(?:California\s+)?Evidence\s+Code\s*(?:\(Evid\.?\s*Code\))?\s*§?\s*(\d+(?:\.\d+)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['cal-evid'](match[1]),
      text: match[0],
      title: `California Evidence Code § ${match[1]}`
    })
  },
  
  // US Code
  {
    pattern: /\b(?:U\.?S\.?C\.?|United\s+States\s+Code)\s*(?:Title\s+|§\s*)?(\d+)\s*§?\s*(\d+(?:\w)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['usc'](match[1], match[2]),
      text: match[0],
      title: `${match[1]} U.S.C. § ${match[2]}`
    })
  },
  
  // Code of Federal Regulations
  {
    pattern: /\b(?:C\.?F\.?R\.?|Code\s+of\s+Federal\s+Regulations)\s*(?:Title\s+|§\s*)?(\d+)\s*§?\s*(\d+(?:\.\d+)*)/gi,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['cfr'](match[1], match[2]),
      text: match[0],
      title: `${match[1]} C.F.R. § ${match[2]}`
    })
  },
  
  // Case Citations (simplified - just search)
  {
    pattern: /\b([A-Z][a-zA-Z\s&,]+)\s+v\.\s+([A-Z][a-zA-Z\s&,]+)\s*(?:\((\d{4})\))?\s*(?:(\d+\s+[A-Za-z\.]+\s+\d+))?/g,
    generator: (match: RegExpMatchArray) => ({
      url: LEGAL_URL_GENERATORS['case-search'](`${match[1]} v. ${match[2]} ${match[3] || ''}`),
      text: match[0],
      title: `Search for case: ${match[1]} v. ${match[2]}`
    })
  }
];

// Auto-link legal references in text
const autoLinkLegalReferences = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const allMatches: Array<{
    start: number;
    end: number;
    url: string;
    text: string;
    title: string;
  }> = [];

  // Find all legal reference matches
  LEGAL_REFERENCE_PATTERNS.forEach(({ pattern, generator }) => {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
      const linkInfo = generator(match);
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        url: linkInfo.url,
        text: linkInfo.text,
        title: linkInfo.title
      });
    }
  });

  // Sort matches by position and handle overlaps
  allMatches.sort((a, b) => a.start - b.start);
  const filteredMatches = allMatches.filter((match, index) => {
    for (let i = 0; i < index; i++) {
      if (allMatches[i].end > match.start) {
        return false; // Overlapping match, skip
      }
    }
    return true;
  });

  // Build the result with text and linked components
  filteredMatches.forEach((match, index) => {
    // Add text before this match
    if (match.start > lastIndex) {
      parts.push(text.slice(lastIndex, match.start));
    }
    
    // Add the linked legal reference
    parts.push(
      <a
        key={`legal-link-${index}`}
        href={match.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-lex-accent hover:text-lex-accent-hover underline transition-colors bg-lex-accent-soft px-1.5 py-0.5 rounded text-xs font-medium border border-lex-accent"
        title={match.title}
      >
        🔗 {match.text}
      </a>
    );
    
    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

/**
 * Legal formatting utility functions
 */

// Process text with legal patterns and return JSX elements
const processLegalFormatting = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const allMatches: Array<{
    start: number;
    end: number;
    type: string;
    text: string;
    component: React.ReactNode;
  }> = [];

  // Legal citation patterns
  const LEGAL_CITATION_PATTERNS = [
    /\b([A-Z][a-zA-Z\s&,]+)\s+v\.\s+([A-Z][a-zA-Z\s&,]+)\s+\((\d{4})\)\s+(\d+\s+[A-Za-z\.]+\s+\d+)/g,
    /\b(\d+\s+[A-Z]\.?[A-Z]\.?[A-Z]\.?\s*§\s*\d+[a-z]?)/g,
    /\b([A-Za-z]+\.\s+[A-Za-z]+\.\s+Code\s+§\s*\d+[a-z]?)/g,
  ];

  // Clause label patterns
  const CLAUSE_LABEL_PATTERNS = [
    /\b(§\s*\d+[a-z]?(?:\.\d+)*)/g,
    /\b(Exh?\.\s*[A-Z]\b)/g,
    /\b(Art\.\s*[IVX]+)/g,
    /\b(¶\s*\d+)/g,
    /\b(Sched\.\s*[A-Z])/g,
  ];

  // Line number pattern
  const LINE_NUMBER_PATTERN = /\b(\d+)[–—-](\d+)\b/g;

  // Decorator patterns
  const DECORATOR_PATTERNS = [
    { pattern: /\[CONFIDENTIAL\]/gi, type: 'confidential', icon: Lock },
    { pattern: /\[PRIVILEGED\]/gi, type: 'privileged', icon: Shield },
    { pattern: /\[ATTORNEY[- ]WORK[- ]PRODUCT\]/gi, type: 'work-product', icon: Eye },
    { pattern: /\[DRAFT\]/gi, type: 'draft', icon: AlertTriangle },
  ];

  // Find all legal citations
  LEGAL_CITATION_PATTERNS.forEach(pattern => {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'citation',
        text: match[0],
        component: (
          <span className="bg-lex-accent-soft text-lex-accent px-1.5 py-0.5 rounded text-xs font-medium border border-lex-accent">
            {match[0]}
          </span>
        )
      });
    }
  });

  // Find all clause labels
  CLAUSE_LABEL_PATTERNS.forEach(pattern => {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'clause',
        text: match[0],
        component: (
          <span className="bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-1.5 py-0.5 rounded text-xs font-medium border border-purple-200 dark:border-purple-700">
            {match[0]}
          </span>
        )
      });
    }
  });

  // Find line numbers
  let match;
  LINE_NUMBER_PATTERN.lastIndex = 0;
  while ((match = LINE_NUMBER_PATTERN.exec(text)) !== null) {
    allMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'line-number',
      text: match[0],
      component: (
        <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded text-xs font-mono border border-gray-300 dark:border-gray-600">
          Lines {match[1]}–{match[2]}
        </span>
      )
    });
  }

  // Find decorators
  DECORATOR_PATTERNS.forEach(({ pattern, type, icon: IconComponent }) => {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
      const colorMap = {
        'confidential': 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700',
        'privileged': 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
        'work-product': 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700',
        'draft': 'bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700',
      };

      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'decorator',
        text: match[0],
        component: (
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorMap[type as keyof typeof colorMap] || 'bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600'}`}>
            <IconComponent className="w-3 h-3 mr-1" />
            {match[0]}
          </span>
        )
      });
    }
  });

  // Sort matches by position and handle overlaps
  allMatches.sort((a, b) => a.start - b.start);
  const filteredMatches = allMatches.filter((match, index) => {
    for (let i = 0; i < index; i++) {
      if (allMatches[i].end > match.start) {
        return false; // Overlapping match, skip
      }
    }
    return true;
  });

  // Build the result with text and components
  filteredMatches.forEach((match, index) => {
    // Add text before this match
    if (match.start > lastIndex) {
      parts.push(text.slice(lastIndex, match.start));
    }
    
    // Add the formatted component
    parts.push(
      <React.Fragment key={`legal-${match.type}-${index}`}>
        {match.component}
      </React.Fragment>
    );
    
    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

/**
 * LexMessageRenderer - Rich text renderer for Lex AI messages
 * 
 * Provides ChatGPT-style markdown rendering with:
 * - Clean typography and spacing
 * - Code syntax highlighting
 * - Legal-friendly formatting
 * - Enhanced readability for busy legal professionals
 * - Legal citations, clause labels, line numbers, and decorators
 * - Smart emoji/intent parsing
 * - Contextual styling based on message role
 */
export const LexMessageRenderer: React.FC<LexMessageRendererProps> = ({ 
  content, 
  className = '',
  enableLegalFormatting = true,
  enableSmartEmojis = true,
  role = 'assistant'
}) => {
  // Apply smart emoji parsing first
  const emojiProcessedContent = withSmartEmojis(content, enableSmartEmojis);
  
  // Get contextual styling for the role
  const styling = getContextualStyling(role);
  const contextualComponents = getContextualMarkdownComponents(role);

  // For simple approach: process content for legal formatting first, then markdown
  const processedContent = enableLegalFormatting ? (
    <div className="space-y-2">
      {emojiProcessedContent.split('\n\n').map((paragraph, index) => {
        // Check if this paragraph contains markdown syntax
        const hasMarkdown = /[*_`#\[\]]/g.test(paragraph);
        
        if (hasMarkdown) {
          // Render as markdown
          return (
            <div key={index}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={contextualComponents}
              >
                {paragraph}
              </ReactMarkdown>
            </div>
          );
        } else {
          // Process for legal formatting and auto-linking
          const autoLinkedParts = autoLinkLegalReferences(paragraph);
          const legalParts = autoLinkedParts.flatMap(part => {
            if (typeof part === 'string') {
              return processLegalFormatting(part);
            }
            return [part]; // Already a React component (link)
          });
          return (
            <p key={index} className={`text-sm ${styling.textColor} leading-relaxed`}>
              {legalParts}
            </p>
          );
        }
      })}
    </div>
  ) : (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={contextualComponents}
    >
      {emojiProcessedContent}
    </ReactMarkdown>
  );

  return (
    <div className={`prose prose-sm max-w-none ${styling.prose} ${styling.container} ${styling.shadow} p-4 rounded-lg ${className}`}>
      {/* Role indicator */}
      <div className="flex items-center mb-2 text-xs font-medium opacity-75">
        <span className="mr-1">{styling.icon}</span>
        <span className={styling.accentColor}>{styling.label}</span>
      </div>
      
      {processedContent}
    </div>
  );
};

export default LexMessageRenderer;